import React, { Component } from "react";
import PropTypes from "prop-types";

import LoadingOverlay from "./LoadingOverlay";
import { handleError } from "@/util/errorHandler";
import {
  renderTotal,
  renderPaginationPanel,
  renderNoData,
} from "@/util/paginationHelper";
import { debounce } from "lodash";
import ExportReportButton from "@/views/Components/ExportReport/ExportReportButton";

class TableContainer extends Component {
  state = {
    page: 1,
    sizePerPage: 10,
    filter: "",
    sort: "",
    data: [],
    total: 0,
    isLoading: false,
  };

  createExportReportButton = () => {
    const params = {
      page: this.state.page - 1,
      size: this.state.sizePerPage,
      filter: this.state.filter,
      sort: this.state.sort,
    };

    return (
      <ExportReportButton
        params={params}
        requestReport={this.props.requestReport}
        disableDateRangePicker={this.props.reportWithoutDateRange}
        initialDates={this.props.reportWithInitialDates}
      />
    );
  };

  loadAndStoreData = async (
    page = 0,
    sizePerPage = 10,
    filter = "",
    sort = "",
  ) => {
    this.setState({ isLoading: true });

    try {
      const { data, total } = await this.props.loadData(
        page,
        sizePerPage,
        filter,
        sort,
      );
      this.setState({ data, total });
    } catch (error) {
      let errorMsg = error.response
        ? error.response.data.message
        : "Error Unknown";
      handleError(new Error(errorMsg));
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleUpdateCurrentPage = () => {
    this.loadAndStoreData(
      this.state.page - 1,
      this.state.sizePerPage,
      this.state.filter,
      this.state.sort,
    );
  };

  handlePageChange = (page, sizePerPage) => {
    this.setState({ page, sizePerPage });
    this.loadAndStoreData(
      page - 1,
      sizePerPage,
      this.state.filter,
      this.state.sort,
    );
  };

  handleSizePerPageChange = sizePerPage => {
    this.setState({ sizePerPage });
    this.loadAndStoreData(
      this.state.page - 1,
      sizePerPage,
      this.state.filter,
      this.state.sort,
    );
  };

  handleFilterChange = filter => {
    this.setState({ filter, page: 1 });
    this.loadAndStoreData(0, this.state.sizePerPage, filter, this.state.sort);
  };

  handleSortChange = (sortName, sortOrder) => {
    const sort = `${sortName},${sortOrder}`;

    this.setState({ sort });
    this.loadAndStoreData(
      this.state.page - 1,
      this.state.sizePerPage,
      this.state.filter,
      sort,
    );
  };

  componentDidMount() {
    if (this.props.autoLoad) {
      this.loadAndStoreData();
    }
  }

  /*
   * This function is used to reload data if external parameters, passed to loadData, change
   * We need to switch to the first page but save everything, so we can use handlePageChange :)
   */
  handleExternalParametersChange = () => {
    this.handlePageChange(1, this.state.sizePerPage);
  };

  render() {
    const { page, sizePerPage } = this.state;

    const options = {
      sizePerPageList: [
        {
          text: "5",
          value: 5,
        },
        {
          text: "10",
          value: 10,
        },
        {
          text: "25",
          value: 25,
        },
        {
          text: "50",
          value: 50,
        },
      ],
      onPageChange: this.handlePageChange,
      onSizePerPageList: this.handleSizePerPageChange,
      onSearchChange: debounce(this.handleFilterChange, 1000),
      onSortChange: this.handleSortChange,
      page,
      sizePerPage,
      hidePageListOnlyOnePage: true,
      paginationShowsTotal: renderTotal,
      paginationPanel: renderPaginationPanel,
      noDataText: renderNoData(this.state.isLoading),
      exportCSVBtn: this.createExportReportButton,
    };

    const fetchInfo = { dataTotalSize: this.state.total };

    // TODO: Maybe move this loader into the table, as it is done here:
    // https://stackoverflow.com/questions/43338884/how-add-react-loading-in-react-bootstrap-table
    return (
      <div style={{ position: "relative" }}>
        <LoadingOverlay loading={this.state.isLoading} />
        {React.cloneElement(this.props.children, {
          options: options,
          data: this.state.data,
          fetchInfo: fetchInfo,
        })}
      </div>
    );
  }
}

TableContainer.defaultProps = {
  autoLoad: true,
  reportWithoutDateRange: false,
};

TableContainer.propTypes = {
  loadData: PropTypes.func,
  requestReport: PropTypes.func.isRequired,
  children: PropTypes.element.isRequired,
  autoLoad: PropTypes.bool,
  reportWithoutDateRange: PropTypes.bool,
  reportWithInitialDates: ExportReportButton.propTypes.initialDates,
};

export default TableContainer;
